import React from 'react';

import { FalhaFipeItem, ROTULO_ETAPA_FALHA } from '../../../api/fipeSync/fipeSyncApi';
import ModalSync from '../../../components/sync-painel/ModalSync';
import { formatarDataHora } from '../../../components/sync-painel/statusBadge';

/** O que o reprocessamento refaz, por fase — o mesmo que a hint e o backend descrevem. */
const O_QUE_REFAZ: Record<string, string> = {
  modelos:
    'Refaz os modelos desta marca e, em cascata, os anos e os preços dela — porque quando os modelos falham, o que vem depois fica desatualizado junto.',
  anos: 'Refaz os anos deste modelo e, em cascata, os preços deles.',
  veiculos: 'Refaz apenas o preço FIPE desta combinação de marca, modelo e ano.',
};

/**
 * Detalhe de uma pendência: o escopo exato que ficou de fora e o motivo que a FIPE deu.
 * O motivo importa — instabilidade passageira basta reprocessar; lista incompleta
 * recorrente costuma ser problema do lado da FIPE, e insistir não resolve.
 */
const FalhaFipeModal: React.FC<{
  item: FalhaFipeItem;
  onClose: () => void;
  onReprocessar: (item: FalhaFipeItem) => void;
  reprocessando: boolean;
}> = ({ item, onClose, onReprocessar, reprocessando }) => {
  const escopo = item.escopo ?? ({} as FalhaFipeItem['escopo']);
  const tipoVeiculo = escopo.vehicleType === 'motorcycle' ? 'Moto' : 'Carro';

  return (
    <ModalSync
      titulo={`Pendência em ${ROTULO_ETAPA_FALHA[item.etapa] ?? item.etapa}`}
      subtitulo={`Registrada em ${formatarDataHora(item.criadoEm)}`}
      onClose={onClose}
      largura="min(720px, 94vw)"
      footer={
        <>
          <button type="button" className="filtro-btn-secondary" onClick={onClose}>
            Fechar
          </button>
          <button
            type="button"
            className="filtro-btn-primary"
            disabled={reprocessando}
            onClick={() => onReprocessar(item)}
          >
            {reprocessando ? 'Reprocessando...' : 'Reprocessar esta pendência'}
          </button>
        </>
      }
    >
      <div className="filtro-section">
        <h3 className="filtro-section-title">O que ficou de fora</h3>
        <div className="admin-linha-item">
          <span className="admin-texto-secundario">Marca</span>
          <span>
            {escopo.marcaNome} <span className="admin-texto-secundario">({escopo.marcaCode})</span>
          </span>
        </div>
        {escopo.modeloNome && (
          <div className="admin-linha-item">
            <span className="admin-texto-secundario">Modelo</span>
            <span>
              {escopo.modeloNome} <span className="admin-texto-secundario">({escopo.modeloCode})</span>
            </span>
          </div>
        )}
        {escopo.anoNome && (
          <div className="admin-linha-item">
            <span className="admin-texto-secundario">Ano</span>
            <span>{escopo.anoNome}</span>
          </div>
        )}
        <div className="admin-linha-item">
          <span className="admin-texto-secundario">Tipo</span>
          <span>{tipoVeiculo}</span>
        </div>
      </div>

      <div className="filtro-section">
        <h3 className="filtro-section-title">Motivo</h3>
        <p style={{ margin: 0 }}>{item.motivo}</p>
        <p className="admin-hint" style={{ marginBottom: 0 }}>
          Os dados anteriores deste item continuam disponíveis no sistema — eles apenas não foram atualizados
          nesta execução.
        </p>
      </div>

      <div className="filtro-section">
        <h3 className="filtro-section-title">O que o reprocessamento faz</h3>
        <p style={{ margin: 0 }}>{O_QUE_REFAZ[item.etapa] ?? 'Refaz apenas este item.'}</p>
        <p className="admin-hint" style={{ marginBottom: 0 }}>
          Roda em segundo plano e aparece no histórico como Reprocessamento. Se falhar de novo, a pendência
          volta para a lista com o motivo atualizado.
        </p>
      </div>
    </ModalSync>
  );
};

export default FalhaFipeModal;
